/**
 * Figure frames drawn off the main thread. A crowd coming into view (a guild war) needs hundreds of
 * frames; drawn on the main thread, even a few a frame make the game stutter. A few workers draw
 * them instead (frameWorker.ts) and hand back finished pictures, while frames already drawn stand
 * in. Where workers or offscreen canvases are missing, everything is drawn on the main thread.
 */
import { figureOutlineMessage } from '../figureOutlineFlag';
import { truePxMessage } from '../truepx/flags';
import { densityMessage } from '../density';
import { atRes, canvas, ctx2d } from '../pixel';
import type { Look, Pose, View8 as View } from '../sprites';
import type { BladeLine } from './weapons';

export interface FrameRequest {
  look: Look;
  /** Names the look, so a worker builds each figure once. */
  key: string;
  view: View;
  pose: Pose;
  ph: number;
  lag: number;
  blink: boolean;
}

/** What a worker sends back: the picture and where it was cut from the full frame. */
export interface FrameReply {
  id: number;
  bitmap: ImageBitmap;
  res: number;
  x: number;
  y: number;
  blades: BladeLine[];
}

type Done = (c: HTMLCanvasElement, blades: BladeLine[]) => void;
/** A frame asked for: still waiting in `lane`, or `sent` to a worker. */
export interface Job {
  id: number;
  req: FrameRequest;
  done: Done;
  lane: Lane;
  sent?: boolean;
  /** Drawn ahead of need and back from its worker, waiting for idle time to be handed over. */
  parked?: FrameReply;
}

/**
 * Which frames are drawn first, most pressing first:
 * - `mine`: the player's own frames being shown right now (the figure stands still until they come);
 * - `urgent`: a figure with nothing drawn yet (shown as nothing);
 * - `mineCore`: the player's walk, run, stance and idle cycles, drawn ahead of need in every view;
 * - `now`: other figures' frames being shown right now, a stand-in in their place;
 * - `mineWarm`: the rest of the player's actions (strikes, skills, emotes, fabric), ahead of need;
 * - `later`: everything else (other figures' frames asked for ahead of need, or once).
 */
export const LANES = ['mine', 'urgent', 'mineCore', 'now', 'mineWarm', 'later'] as const;
export type Lane = (typeof LANES)[number];
const RANK: Record<Lane, number> = { mine: 0, urgent: 1, mineCore: 2, now: 3, mineWarm: 4, later: 5 };

/** Frames a worker is given at once: enough to keep it busy, few enough that urgent ones jump ahead. */
const IN_FLIGHT = 2;

let pool: { w: Worker; busy: number }[] | null | undefined;
/** Waiting jobs by lane (a job moved to a more pressing lane is left behind in the old one, and skipped). */
const lanes: Job[][] = LANES.map(() => []);
const out = new Map<number, Job>();
let nextId = 1;

/** Frames asked of the workers so far, and how many are still to come back (for the loading screen). */
export const frameJobs = { asked: 0, waiting: 0 };

function start(): typeof pool {
  if (pool !== undefined) return pool;
  // Not from inside a worker, and only where workers can draw.
  if (
    typeof document === 'undefined' ||
    typeof Worker === 'undefined' ||
    typeof OffscreenCanvas === 'undefined'
  )
    return (pool = null);
  try {
    const n = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
    pool = Array.from({ length: n }, () => {
      const w = new Worker(new URL('./frameWorker.ts', import.meta.url), { type: 'module' });
      // The page's pixel-density mode (density.ts), before any frame is asked for.
      w.postMessage(densityMessage());
      // Lane C0-TRUEPX-GAME: whether this page's figures get the true-pixel clean-up (fixed per page).
      w.postMessage(truePxMessage());
      // The figure outline style (figureOutline.setting.ts), fixed per page.
      w.postMessage(figureOutlineMessage());
      const slot = { w, busy: 0 };
      w.onmessage = (e: MessageEvent<FrameReply>) => {
        slot.busy--;
        const r = e.data;
        const job = out.get(r.id);
        out.delete(r.id);
        if (!job) r.bitmap.close();
        // A frame wanted soon is handed over at once; one drawn ahead of need waits for idle time.
        else if (RANK[job.lane] < RANK.mineWarm) land(job, r);
        else {
          job.parked = r;
          slow.push(job);
          idleLand();
        }
        feed();
      };
      w.onerror = () => fail();
      return slot;
    });
  } catch {
    pool = null;
  }
  return pool;
}

/** A worker's frame handed to whoever asked for it (and to the frame store). */
function land(job: Job, r: FrameReply): void {
  job.parked = undefined;
  frameJobs.waiting--;
  const c = canvas(r.bitmap.width, r.bitmap.height);
  ctx2d(c).drawImage(r.bitmap, 0, 0);
  r.bitmap.close();
  const frame = atRes(c, r.res, r.x, r.y);
  drawn?.(job.req, frame, r.blades);
  job.done(frame, r.blades);
}

/**
 * Frames drawn ahead of need (`mineWarm`, `later`), back from the workers and not handed over yet.
 * Each hand-over makes a canvas on the main thread: thousands of them in a burst after the
 * loading screen, at a worker's pace, crowded the game's frames and fed the garbage collector in
 * bursts (docs/review/perf/2026-10-13-hitch-trace.md). They are handed over in idle time instead,
 * and no more such frames are sent to the workers while `SLOW_MAX` wait here.
 */
const slow: Job[] = [];
let slowHead = 0;
const SLOW_MAX = 8;
/** At most this many a turn when the browser has had no idle time to give (a busy game still catches up). */
const SLOW_FORCED = 2;
let idling = false;
const idle: (f: IdleRequestCallback, o: IdleRequestOptions) => unknown =
  typeof requestIdleCallback === 'function'
    ? (f, o) => requestIdleCallback(f, o)
    : (f) => setTimeout(() => f({ didTimeout: true, timeRemaining: () => 0 }), 16);
const slowLeft = (): number => slow.length - slowHead;
function idleLand(): void {
  if (idling) return;
  idling = true;
  idle(pumpSlow, { timeout: 100 });
}
function pumpSlow(d: IdleDeadline): void {
  let forced = d.didTimeout ? SLOW_FORCED : 0;
  while (slowLeft() && (d.timeRemaining() > 2 || forced-- > 0)) {
    const job = slow[slowHead]!;
    slow[slowHead++] = undefined as never;
    // Handed over already if it was hurried meanwhile.
    if (job.parked) land(job, job.parked);
  }
  if (slowHead === slow.length) slow.length = slowHead = 0;
  idling = false;
  if (slowLeft()) idleLand();
  feed();
}

/** A worker failed to load or crashed: draw on the main thread from now on, starting with what was waiting. */
let onFail: ((req: FrameRequest, done: Done, first: boolean) => void) | null = null;
function fail(): void {
  // Drawn already: handed over as they are.
  while (slowLeft()) {
    const job = slow[slowHead++]!;
    if (job.parked) land(job, job.parked);
  }
  slow.length = slowHead = 0;
  const jobs = [...out.values(), ...lanes.flat().filter((j, i, all) => !j.sent && all.indexOf(j) === i)];
  for (const p of pool ?? []) p.w.terminate();
  pool = null;
  out.clear();
  for (const l of lanes) l.length = 0;
  // Handed to the main thread, where they are counted as idle-time work (sets.ts) instead.
  frameJobs.waiting = 0;
  for (const j of jobs) onFail?.(j.req, j.done, RANK[j.lane] <= RANK.urgent);
}

/** The most pressing job still waiting. */
function take(): Job | undefined {
  // Frames ahead of need wait their turn while the ones already drawn still wait to be handed over.
  const last = slowLeft() >= SLOW_MAX ? RANK.mineWarm : lanes.length;
  for (let r = 0; r < last; r++) {
    const lane = lanes[r]!;
    while (lane.length) {
      const job = lane.shift()!;
      // Moved on to a more pressing lane (and perhaps sent from there) since it was queued here.
      if (!job.sent && RANK[job.lane] === r) return job;
    }
  }
  return undefined;
}

function feed(): void {
  if (!pool) return;
  for (const slot of pool)
    while (slot.busy < IN_FLIGHT) {
      const job = take();
      if (!job) return;
      job.sent = true;
      out.set(job.id, job);
      slot.busy++;
      slot.w.postMessage({ id: job.id, ...job.req });
    }
}

/** Whether frames are being drawn by workers. */
export const workersOn = (): boolean => !!start();

/**
 * Asks a worker for a frame; `done` gets it when it arrives. Null when there are no workers (the
 * caller draws it itself). `lane` says how soon it is needed (see `LANES`).
 */
export function drawElsewhere(req: FrameRequest, done: Done, lane: Lane): Job | null {
  if (!start()) return null;
  const job: Job = { id: nextId++, req, done, lane };
  frameJobs.asked++;
  frameJobs.waiting++;
  lanes[RANK[lane]]!.push(job);
  feed();
  return job;
}

/** Moves a waiting job up to a more pressing lane: it is being shown and is not drawn yet. */
export function hurry(job: Job, lane: Lane): void {
  // Drawn and waiting for idle time: wanted now, so handed over now.
  if (job.parked) return land(job, job.parked);
  if (job.sent || RANK[lane] >= RANK[job.lane]) return;
  job.lane = lane;
  lanes[RANK[lane]]!.push(job);
  feed();
}

/** Told of every frame a worker hands back (frameCache.ts keeps them in this browser). */
let drawn: ((req: FrameRequest, c: HTMLCanvasElement, blades: BladeLine[]) => void) | null = null;
export function whenFrameDrawn(f: (req: FrameRequest, c: HTMLCanvasElement, blades: BladeLine[]) => void): void {
  drawn = f;
}

/** What to do with requests left over if the workers fail (draw them on the main thread). */
export function whenWorkersFail(f: (req: FrameRequest, done: Done, first: boolean) => void): void {
  onFail = f;
}
